'use client'

import {useSearchParams} from 'next/navigation'
import {FC, PropsWithChildren} from 'react'

import {PreferencesProvider} from '@/components/context/Preferences'
import {CookieRecord} from '@/utils/preferences/types'

/*
 * Two implementations of the preferences provider, one per rendering mode. (The A/B provider needs
 * none: it assigns in the browser on every page - `components/context/ABTest.tsx`.)
 *
 * The request variant reads the query string with `useSearchParams()`, exactly as the shell always
 * has, so a `?currency=USD` link is already correct in the server-rendered HTML. That hook is fine
 * on a dynamically rendered route but forbidden — outside a `<Suspense>` boundary — on a
 * prerendered one, and wrapping the shell in Suspense would leave the static HTML with nothing in
 * it.
 *
 * The browser variant therefore reads the same values from `window.location` after mount instead.
 * They are separate components rather than one branching component so that neither ever calls a
 * hook conditionally.
 */

type PreferencesProps = PropsWithChildren<{
  host: string
  cookies: CookieRecord
  country?: string
}>

export const RequestPreferences: FC<PreferencesProps> = ({host, cookies, country, children}) => {
  const searchParams = useSearchParams()
  const params = searchParams ? Object.fromEntries(searchParams.entries()) : {}

  return (
    <PreferencesProvider host={host} cookies={cookies} params={params} country={country}>
      {children}
    </PreferencesProvider>
  )
}

export const BrowserPreferences: FC<PreferencesProps> = ({children}) => (
  <PreferencesProvider host="" cookies={{}} params={{}} resolveOnClient>
    {children}
  </PreferencesProvider>
)
